import { useState, useEffect, useCallback, useRef } from 'react'

interface UsePollingOptions<T> {
  fetcher: () => Promise<T>
  interval?: number
  enabled?: boolean
}

export function usePolling<T>({ fetcher, interval = 5000, enabled = true }: UsePollingOptions<T>) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)
  const fetcherRef = useRef(fetcher)
  const inFlightRef = useRef(false)
  const generationRef = useRef(0)

  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return

    inFlightRef.current = true
    const gen = generationRef.current
    try {
      const result = await fetcherRef.current()
      if (mountedRef.current && gen === generationRef.current) {
        setData(result)
        setError(null)
      }
    } catch (err) {
      if (mountedRef.current && gen === generationRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch data'))
      }
    } finally {
      inFlightRef.current = false
      if (mountedRef.current && gen === generationRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    if (!enabled) {
      setLoading(false)
      return
    }

    fetchData()

    const pollInterval = setInterval(fetchData, interval)

    return () => {
      mountedRef.current = false
      clearInterval(pollInterval)
    }
  }, [fetchData, interval, enabled])

  const refetch = useCallback(() => {
    // Bump generation so any in-flight poll response is discarded
    generationRef.current += 1
    inFlightRef.current = false
    setLoading(true)
    return fetchData()
  }, [fetchData])

  return { data, loading, error, refetch }
}
